import React, { useEffect, useRef } from "react";
import { Tabs, Tab, makeStyles } from "@material-ui/core";
import { TabPanel, TabContext } from "@material-ui/lab";
import theme from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import InsightsHeader from "./insights-header";
import InsightsSidePanelContainer from "./insights-side-panel-container";
import { InsightsActions } from "constants/analytics";
import type { IStore } from "reducers";
import { INSIGHTS } from "constants/integration/insights";

const useStyles = makeStyles({
    tabRoot: {
        padding: "24px 0px",
    },
    tabs: {
        "& button": {
            minWidth: "25%",
        },
        "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "1.3rem",
            color: theme.palette.textColor,
        },
    },
    tabsIndicator: {
        backgroundColor: "#2dbeb4",
    },
    learnMore: {
        fontSize: "1.3rem",
        textDecorationLine: "underline",
        textUnderlineOffset: "5px",
        color: theme.palette.primary1Color,
        display: "block",
        marginTop: "20px",
    },
});

interface IOrderInsightsProductStylesProps {
    onToggleInsightsSidePanel: () => void;
    tabs: {
        label: string;
        key: string;
        component: JSX.Element;
    }[];
    insights?: IStore["insights"];
}

const getDefaultTab = (tabs, insights) => {
    const hasTopProducts = insights.productStyles?.immediate?.length > 0 || insights.productStyles?.prebook?.length > 0;

    if (hasTopProducts) {
        return tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.STYLE_INSIGHTS);
    }

    const hasCategory = insights.categories?.length > 0;

    if (hasCategory) {
        return tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.CATEGORY_INSIGHTS);
    }

    const hasColor = insights.productColors !== null;

    if (hasColor) {
        return tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.COLOR_INSIGHTS);
    }

    return tabs[0];
};

const OrderInsightsPanelContent: React.FC<IOrderInsightsProductStylesProps> = ({
    onToggleInsightsSidePanel,
    tabs,
    insights = null,
}) => {
    const classes = useStyles();

    const [currentTab, setCurrentTab] = React.useState(tabs[0].label);
    const [defaultTabHasBeenSet, setDefaultTabHasBeenSet] = React.useState(false);

    useEffect(() => {
        if (
            insights &&
            !defaultTabHasBeenSet &&
            !insights.isLoading.productStyles &&
            !insights.isLoading.colors &&
            !insights.isLoading.categories
        ) {
            setCurrentTab(insights ? getDefaultTab(tabs, insights)?.label : tabs[0].label);
            setDefaultTabHasBeenSet(true);
        }
    }, [insights?.isLoading]);

    const handleCurrentTabChange = (_, newValue) => {
        global.analytics.track(InsightsActions.tabClick, {
            Tab: newValue,
        });
        setCurrentTab(newValue);
    };

    const handleHelpdeskClick = async (e) => {
        e.preventDefault();
        await global.analytics.track(InsightsActions.helpdeskClick);
        window.open(
            "https://helpdesk.nuorder.com/hc/en-us/articles/27071765927195-Order-Insights-overview-beta",
            "_blank",
            "noopener,noreferrer"
        );
    };

    // Fix the issue when opening closing the PDP drawer
    // This hack allows to update the tab indicator on container resize
    const containerRef = useRef(null);
    const actionRef = useRef(null);
    useEffect(() => {
        const ro = new ResizeObserver(() => {
            if (actionRef.current && containerRef.current) {
                actionRef.current.updateIndicator();
            }
        });

        if (containerRef.current) {
            ro.observe(containerRef.current);
        }

        return () => {
            ro.disconnect();
        };
    }, []);

    return (
        <InsightsSidePanelContainer ref={containerRef}>
            <TabContext value={currentTab}>
                <InsightsHeader onToggleInsightsSidePanel={onToggleInsightsSidePanel}>
                    <Tabs
                        action={actionRef}
                        value={currentTab}
                        onChange={handleCurrentTabChange}
                        classes={{ root: classes.tabs, indicator: classes.tabsIndicator }}
                        centered
                        variant="fullWidth"
                        className={classes.tabs}
                    >
                        {tabs.map((tab, index) => (
                            <Tab key={index} label={tab.label} value={tab.label} />
                        ))}
                    </Tabs>
                </InsightsHeader>
                {tabs.map((tab, index) => (
                    <TabPanel key={index} value={tab.label} classes={{ root: classes.tabRoot }}>
                        {tab.component}
                        <a className={classes.learnMore} onClick={handleHelpdeskClick}>
                            {formatMessage("Learn more about Order Trends")}
                        </a>
                    </TabPanel>
                ))}
            </TabContext>
        </InsightsSidePanelContainer>
    );
};

export default OrderInsightsPanelContent;
